import { forwardRef, memo, useImperativeHandle, useEffect, useRef, useState } from 'react';
import { withHistory } from 'slate-history';
import { Slate, ReactEditor, withReact } from 'slate-react';
import { createEditor, Editor, Transforms, Descendant } from 'slate';
import { useDropzone } from 'react-dropzone';
import FileDropzone from '@sdk/attachments/components/FileDropzone';
import useAttachments from '@sdk/attachments/hooks/useAttachments';
import useToast from '@sdk/common/toast/useToast';

import { getInitialValue } from '@web/richText/textEditor.utils';
import { withSling } from '@web/richText/helpers/withSling';
import { useMentions } from '@web/richText/TextEditorInner/hooks/useMentions';

import TextEditorInner, { Props } from './TextEditorInner/TextEditorInner';
import { Wrapper } from './textEditor.styles';
import { RichTextProvider } from './components/RichTextProvider/RichTextProvider';

export type ForwardedAttrs = {
  focus: () => void;
};

const TextEditor = memo(
  forwardRef<ForwardedAttrs, Props>((props, ref) => {
    const editorRef = useRef<Editor>(null);
    if (!editorRef.current) {
      editorRef.current = withHistory(withSling(withReact(createEditor())));
    }
    const editor = editorRef.current;
    const { attachmentsDisabled, initialAttachments, initialValue, personas } = props;
    const { showErrorToast } = useToast();
    const [value, setValue] = useState<Descendant[]>(() => getInitialValue(initialValue));
    const mentionsState = useMentions(editor, personas);
    const attachmentsState = useAttachments(initialAttachments, showErrorToast);
    const { getRootProps, isDragActive } = useDropzone({
      onDrop: attachmentsState.addAttachmentsFromFilelist,
    });
    const dropzoneProps = attachmentsDisabled ? {} : getRootProps();

    useImperativeHandle(ref, () => ({
      focus: () => {
        Transforms.select(editor, Editor.end(editor, []));
        ReactEditor.focus(editor);
      },
    }));

    useEffect(() => {
      // https://github.com/ianstormtaylor/slate/pull/4540
      // https://github.com/ianstormtaylor/slate/issues/4689
      const nodes = getInitialValue(initialValue);
      setValue(nodes);
      editor.children = nodes;

      // https://github.com/ianstormtaylor/slate/issues/3858
      return () => {
        Transforms.select(editor, {
          anchor: { path: [0, 0], offset: 0 },
          focus: { path: [0, 0], offset: 0 },
        });
      };
    }, [initialValue, editor]);

    return (
      <RichTextProvider>
        <Wrapper {...dropzoneProps} hasError={props.hasError}>
          <Slate
            editor={editor}
            value={value}
            onChange={(v): void => {
              setValue(v);
              mentionsState.onChangeValue();
            }}>
            <TextEditorInner
              editor={editor}
              value={value}
              mentionsState={mentionsState}
              attachmentsState={attachmentsState}
              {...props}
            />
          </Slate>

          {isDragActive && <FileDropzone />}
        </Wrapper>
      </RichTextProvider>
    );
  })
);

TextEditor.displayName = 'TextEditor';

export default TextEditor;
